import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Filter, Search, X, Pencil } from 'lucide-react';
import EnhancedProjectsTable from './EnhancedProjectsTable';
import { TableColumn, ColumnWidth } from '../../../components/common/EnhancedTable/types';
import './EnhancedProjects.css';
import { supabase } from '../../../lib/supabase';
import { formatTextWithLinks } from '../../../utils/formatters';
import { usePermissions } from '../../../hooks/usePermissions';
import ProjectFormModal from '../../../components/Projects/ProjectFormModal';
import type { Project } from '../../../types/project';
import type { Community } from '../../../types/community';

// Define the sort field type
type SortField = 'proj_id' | 'proj_comm' | 'proj_desc' | 'proj_details';

// Define the sort config type
type SortConfig = {
  field: SortField;
  direction: 'asc' | 'desc';
};

// Define the filter type
type ProjectFilter = {
  community: string | null;
  searchText: string | null;
};

export default function EnhancedProjects() {
  const { canManageSystemSettings } = usePermissions();
  const queryClient = useQueryClient();
  
  // State for filters, sorting, and pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<ProjectFilter>({
    community: null,
    searchText: null
  });
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'proj_comm',
    direction: 'asc'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  
  // State for modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch projects data
  const { data: projects = [], isLoading, error: fetchError } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .order('proj_comm', { ascending: true });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      return data || [];
    }
  });

  // Fetch communities for filtering
  const { data: communities = [] } = useQuery<Community[]>({
    queryKey: ['communities'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('communities')
        .select('*')
        .order('comm_name', { ascending: true });

      if (error) {
        console.error('Error fetching communities:', error);
        throw error;
      }

      return data || [];
    }
  });

  // Extract unique communities from projects
  const uniqueCommunities = useMemo(() => {
    const commIds = new Set(projects.map(project => project.proj_comm));
    return communities.filter(comm => commIds.has(comm.comm_id));
  }, [projects, communities]);

  // Apply filters and search
  const filteredProjects = useMemo(() => {
    if (!projects) return [];

    return projects.filter(project => {
      // Search query filter
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        const matchesSearch = 
          (project.proj_desc?.toLowerCase() || '').includes(searchLower) ||
          (project.proj_details?.toLowerCase() || '').includes(searchLower) ||
          (project.proj_id?.toLowerCase() || '').includes(searchLower);
        
        if (!matchesSearch) return false;
      }

      // Community filter
      if (filters.community && project.proj_comm !== filters.community) {
        return false;
      }

      // Additional text search filter
      if (filters.searchText) {
        const searchLower = filters.searchText.toLowerCase();
        const matchesSearch = 
          (project.proj_desc?.toLowerCase() || '').includes(searchLower) ||
          (project.proj_details?.toLowerCase() || '').includes(searchLower);
        
        if (!matchesSearch) return false;
      }

      return true;
    });
  }, [projects, searchQuery, filters]);

  // Apply sorting
  const sortedProjects = useMemo(() => {
    return [...filteredProjects].sort((a, b) => {
      const direction = sortConfig.direction === 'asc' ? 1 : -1;
      
      // Helper function to safely compare strings, handling null/undefined values
      const safeCompare = (valA: string | null | undefined, valB: string | null | undefined) => {
        // Both null/undefined should be equal (return 0)
        if ((!valA || valA === '') && (!valB || valB === '')) return 0;
        // Null/undefined values should sort after defined values
        if (!valA || valA === '') return 1;
        if (!valB || valB === '') return -1;
        // Normal string comparison
        return valA.localeCompare(valB);
      };
      
      switch (sortConfig.field) {
        case 'proj_id': 
          return direction * safeCompare(a.proj_id, b.proj_id);
        case 'proj_comm': 
          return direction * safeCompare(a.proj_comm, b.proj_comm);
        case 'proj_desc': 
          return direction * safeCompare(a.proj_desc, b.proj_desc);
        case 'proj_details': 
          return direction * safeCompare(a.proj_details, b.proj_details);
        default: 
          return 0;
      }
    });
  }, [filteredProjects, sortConfig]);

  // Apply pagination
  const paginatedProjects = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedProjects.slice(startIndex, endIndex);
  }, [sortedProjects, currentPage, pageSize]);

  // Handle sorting
  const handleSort = (sortConfig: { field: string; direction: 'asc' | 'desc' }) => {
    console.log('Sorting:', sortConfig);
    setSortConfig(sortConfig as SortConfig);
  };

  // Handle sort button clicks
  const handleSortButtonClick = (field: SortField) => {
    setSortConfig(current => ({
      field,
      direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Handle filter changes
  const handleFilterChange = (newFilters: Partial<ProjectFilter>) => {
    setFilters(current => ({ ...current, ...newFilters }));
  };

  // Handle removing a filter
  const handleRemoveFilter = (key: keyof ProjectFilter) => {
    setFilters(prev => ({ ...prev, [key]: null }));
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Handle form submission
  const handleSubmit = async (data: Project) => {
    setError(null);
    setIsSubmitting(true);
    try {
      if (selectedProject) {
        // Update existing project
        const { error: updateError } = await supabase
          .from('projects')
          .update(data)
          .eq('proj_id', selectedProject.proj_id);
        
        if (updateError) throw updateError;
      } else {
        // Add new project with user-provided ID
        const { error: insertError } = await supabase
          .from('projects')
          .upsert(data, { onConflict: 'proj_id' })
          .select();
        
        if (insertError) throw insertError;
      }
      
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      setIsModalOpen(false);
      setSelectedProject(undefined);
    } catch (err) {
      console.error('Error in project submission:', err);
      
      // Extract more detailed error message
      let errorMessage = 'An error occurred';
      
      if (err instanceof Error) {
        errorMessage = err.message;
      }
      
      // If it's a Supabase error with details
      if (typeof err === 'object' && err !== null) {
        if ('message' in err && typeof (err as any).message === 'string') {
          errorMessage = (err as any).message;
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate pagination configuration
  const paginationConfig = {
    currentPage,
    pageSize,
    totalCount: sortedProjects.length,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    pageSizeOptions: [25, 50, 100, 200]
  };

  // Get community name from ID
  const getCommunityName = (commId: string) => {
    const community = communities.find(c => c.comm_id === commId);
    return community ? community.comm_name : commId;
  };

  // Define table columns
  const columns: TableColumn<Project>[] = [
    {
      id: 'actions',
      header: 'Edit',
      accessor: (row) => (
        <div className="action-buttons">
          <button
            onClick={() => {
              setSelectedProject(row);
              setIsModalOpen(true);
            }}
            className="action-button edit"
            title="Edit Project"
          >
            <Pencil className="h-5 w-5" />
          </button>
        </div>
      ),
      width: 'narrow' as ColumnWidth,
      // Only show the Edit column for users with admin privileges
      visibleWhen: (permissions) => permissions.isAdmin || permissions.canManageSystemSettings
    },
    {
      id: 'proj_id',
      header: 'Project ID',
      accessor: (row) => (
        <div className="truncate" title={row.proj_id || ''}>
          {row.proj_id || '-'}
        </div>
      ),
      width: 'wide' as ColumnWidth,
      sortable: true
    },
    {
      id: 'proj_comm',
      header: 'Community',
      accessor: (row) => (
        <div className="truncate" title={row.proj_comm ? getCommunityName(row.proj_comm) : ''}>
          {row.proj_comm ? getCommunityName(row.proj_comm) : '-'}
        </div>
      ),
      width: 'wide' as ColumnWidth,
      sortable: true
    },
    {
      id: 'proj_desc',
      header: 'Description',
      accessor: (row) => (
        <div className="truncate" title={row.proj_desc || ''}>
          {row.proj_desc || '-'}
        </div>
      ),
      width: 'wide' as ColumnWidth,
      sortable: true
    },
    {
      id: 'proj_details',
      header: 'Details',
      accessor: (row) => (
        <div className="truncate" title={row.proj_details || ''}>
          {formatTextWithLinks(row.proj_details || '')}
        </div>
      ),
      width: 'wide' as ColumnWidth,
      sortable: true
    }
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800"></div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="p-4 text-red-500">
        Error loading project data. Please try again later.
      </div>
    );
  }

  return (
    <div className="enhanced-projects-container p-4">
      <div className="enhanced-projects-header">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold">Projects</h1>
            
            {/* Add button - only visible to users with edit permissions */}
            {canManageSystemSettings && (
              <button 
                onClick={() => {
                  setSelectedProject(undefined);
                  setIsModalOpen(true);
                }}
                className="flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Project
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Search input */}
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              />
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
            
            {/* Filter button */}
            <button
              className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              title="Toggle filters"
            >
              <Filter className="h-5 w-5 text-gray-500" />
              <span className="hidden sm:inline">Filters</span>
              {Object.values(filters).filter(Boolean).length > 0 && (
                <span className="ml-1 bg-primary text-white text-xs rounded-full px-2 py-0.5">
                  {Object.values(filters).filter(Boolean).length}
                </span>
              )}
            </button>
            
            <div className="text-base font-bold text-gray-700">
              Total Records: {filteredProjects.length}
            </div>
          </div>

          {/* Active filters */}
          {Object.values(filters).some(Boolean) && (
            <div className="active-filters">
              {filters.community && (
                <div className="filter-tag">
                  <span className="filter-tag-label">Community:</span>
                  <span>{getCommunityName(filters.community)}</span>
                  <button
                    onClick={() => handleRemoveFilter('community')}
                    className="filter-tag-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
              {filters.searchText && (
                <div className="filter-tag">
                  <span className="filter-tag-label">Search:</span>
                  <span>{filters.searchText}</span>
                  <button
                    onClick={() => handleRemoveFilter('searchText')}
                    className="filter-tag-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Filter panel */}
          {isFilterOpen && (
            <div className="filter-panel">
              <div className="filter-panel-header">
                <h3 className="font-medium text-gray-700">Filters</h3>
                <button
                  onClick={() => setFilters({
                    community: null,
                    searchText: null
                  })}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Clear all
                </button>
              </div>
              <div className="filter-panel-body">
                {/* Community filter */}
                <div className="filter-group">
                  <label htmlFor="community-filter">Community</label>
                  <select
                    id="community-filter"
                    value={filters.community || ''}
                    onChange={(e) => handleFilterChange({ community: e.target.value || null })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  >
                    <option value="">All communities</option>
                    {uniqueCommunities.map(community => (
                      <option key={community.comm_id} value={community.comm_id}>
                        {community.comm_name}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Text search filter */}
                <div className="filter-group">
                  <label htmlFor="text-filter">Text Search</label>
                  <input
                    id="text-filter"
                    type="text"
                    value={filters.searchText || ''}
                    onChange={(e) => handleFilterChange({ searchText: e.target.value || null })}
                    placeholder="Search in description and details"
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Sort controls removed - now using column headers for sorting */}
        </div>
      </div>
      
      <div className="enhanced-projects-table-container enhanced-table-force-scroll">
        <EnhancedProjectsTable
          columns={columns}
          data={paginatedProjects}
          keyField="proj_id"
          width="1200px"
          maxHeight="calc(100vh - 20rem)"
          loading={isLoading}
          emptyMessage="No projects found"
          horizontalScroll={true}
          alternateRowColors={true}
          pagination={paginationConfig}
          defaultSort={sortConfig}
          onSort={handleSort}
          permissions={{
            isAdmin: usePermissions().isAdmin,
            canManageSystemSettings: canManageSystemSettings
          }}
        />
      </div>

      {/* Project Form Modal */}
      <ProjectFormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedProject(undefined);
          setError(null);
        }}
        initialData={selectedProject}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        error={error}
        title={selectedProject ? 'Edit Project' : 'Add Project'}
        submitLabel={selectedProject ? 'Save Changes' : 'Add Project'}
      />
    </div>
  );
}
